import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { useAlert } from "./useAlert";
import { getErrorMessage } from "../utils/helpers";

interface INotification {
  type?: "DROPS";
  title?: string;
  subTitle?: string;
  id?: string;
}

export interface NotificationPayload {
  title?: string;
  body?: string;
}
// Lifecycle of a dashboard notification. The API only queues it; a backend
// worker sends it in batches and reports progress here. Rows created before
// this existed have no status and are shown by date, as before.
export type SentNotificationStatus =
  | "PENDING"
  | "RUNNING"
  | "DONE"
  | "FAILED"
  | "CANCELLED";

export interface ISentNotification {
  _id: string;
  data: any;
  notification?: NotificationPayload;
  user?: string | string[];
  scheduleDate?: Date;
  jobId?: string;
  status?: SentNotificationStatus;
  progress?: {
    usersTotal?: number;
    usersDone?: number;
    tokensSent?: number;
    tokensFailed?: number;
  };
  attempts?: number;
  startedAt?: Date | null;
  finishedAt?: Date | null;
  error?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
}

// Sent with every submission so a double click or a retried request collides
// on the backend instead of queueing the same broadcast twice. Generated ONCE
// per compose session by the screen and passed in: generating it here, per
// request, gave every click a fresh key and defeated the whole point.
export const makeIdempotencyKey = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

interface IStore {
  notifications: INotification[];
  sentNotifications: ISentNotification[];
  sentNotificationsLoading: boolean;
  sentNotificationsFetched: boolean;
  sentNotificationsError: string | null;
  createdNotification: string | null;
  /** `silent` refreshes in place (used for progress polling) without
   *  flashing the list spinner. */
  getSentNotifications: (silent?: boolean) => void;
  getNotifications: () => void;
  deleteNotification: (id: string) => void;
  retryNotification: (id: string) => void;
  sendInstantNotificationToAll: (
    notification: NotificationPayload,
    data: any,
    droppedMaterials: string[],
    nonDroppedMaterials: string[],
    usersWithoutDropInLast5Days: boolean,
    allAdmins?: boolean,
    usersWithoutPostcode?: boolean,
    selectedLocations?: string[],
    superAdminsOnly?: boolean,
    /** Created once per compose session by the screen so a double click
     *  reuses it and the backend rejects the second submission. */
    idempotencyKey?: string
  ) => void;
  sendScheduledNotificationToAll: (
    notification: NotificationPayload,
    data: any,
    scheduleDate: Date,
    droppedMaterials: string[],
    nonDroppedMaterials: string[],
    usersWithoutDropInLast5Days: boolean,
    allAdmins?: boolean,
    usersWithoutPostcode?: boolean,
    selectedLocations?: string[],
    superAdminsOnly?: boolean,
    idempotencyKey?: string
  ) => void;
  clearCreatedNotification: () => void;
}

export const useNotifications = create<IStore>()((set) => ({
  notifications: [],
  sentNotifications: [],
  sentNotificationsLoading: true,
  sentNotificationsFetched: false,
  sentNotificationsError: null,
  createdNotification: null,
  getSentNotifications: async (silent = false) => {
    if (!silent) {
      set({ sentNotificationsLoading: true, sentNotificationsError: null });
    }
    try {
      const response = await axios.get("/notifications/sent");
      if (response?.data) {
        set({
          sentNotifications: response.data,
          sentNotificationsFetched: true,
        });
      }
    } catch (error) {
      LOG.error("Error on fetching sent notifications", { error });
      set({
        sentNotificationsError: getErrorMessage(
          error,
          "Failed to load notifications."
        ),
      });
    } finally {
      set({ sentNotificationsLoading: false });
    }
  },
  getNotifications: async () => {
    try {
      const response = await axios.get("/notifications");
      if (response?.data) {
        set({ notifications: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching notifications", { error });
    }
  },
  deleteNotification: async (id: string) => {
    try {
      const response = await axios.delete(`/notifications/${id}`);
      if (response?.data) {
        set((state) => ({
          sentNotifications: state.sentNotifications.filter(
            (item) => item._id !== id
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting notification", { error });
    }
  },
  retryNotification: async (id: string) => {
    try {
      const response = await axios.post(`/notifications/${id}/retry`);
      if (response?.data) {
        set((state) => ({
          sentNotifications: state.sentNotifications.map((item) =>
            item._id === id ? { ...item, ...response.data } : item
          ),
        }));
      }
    } catch (error) {
      LOG.error("Error on retrying notification", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        message: getErrorMessage(error, "Failed to retry the notification."),
        severity: "error",
      });
    }
  },
  sendInstantNotificationToAll: async (
    notification,
    data,
    droppedMaterials,
    nonDroppedMaterials,
    usersWithoutDropInLast5Days,
    allAdmins,
    usersWithoutPostcode,
    selectedLocations,
    superAdminsOnly,
    idempotencyKey
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("/notifications/now/all", {
        notification,
        data,
        droppedMaterials,
        nonDroppedMaterials,
        usersWithoutDropInLast5Days,
        allAdmins,
        usersWithoutPostcode,
        selectedLocations,
        superAdminsOnly,
        idempotencyKey,
      });
      if (response?.data) {
        set((state) => ({
          sentNotifications: [response.data, ...state?.sentNotifications],
          createdNotification: response.data._id,
        }));
      }
    } catch (error: any) {
      LOG.error("Error on sending instant notification", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        message:
          error?.response?.status === 409
            ? "This notification was already queued."
            : getErrorMessage(
                error,
                "Failed to queue notification. Please try again."
              ),
        severity: error?.response?.status === 409 ? "warning" : "error",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  sendScheduledNotificationToAll: async (
    notification,
    data,
    scheduleDate,
    droppedMaterials,
    nonDroppedMaterials,
    usersWithoutDropInLast5Days,
    allAdmins,
    usersWithoutPostcode,
    selectedLocations,
    superAdminsOnly,
    idempotencyKey
  ) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("/notifications/schedule/all", {
        notification,
        data,
        scheduleDate: new Date(scheduleDate),
        droppedMaterials,
        nonDroppedMaterials,
        usersWithoutDropInLast5Days,
        allAdmins,
        usersWithoutPostcode,
        selectedLocations,
        superAdminsOnly,
        idempotencyKey,
      });
      if (response?.data) {
        if (response?.data) {
          set((state) => ({
            sentNotifications: [response.data, ...state?.sentNotifications],
            createdNotification: response.data._id,
          }));
        }
      }
    } catch (error) {
      LOG.error("Error on sending scheduled notification", { error });
      useAlert.getState().setAlert({
        isOpen: true,
        message: getErrorMessage(
          error,
          "Failed to schedule notification. Please try again."
        ),
        severity: "error",
      });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearCreatedNotification: () => {
    set({ createdNotification: null });
  },
}));
